import {
  TypeDocRoot,
  TypeDocNode,
  TypeDocType,
  TypeDocComment,
  TypeDocParameter,
  ResolvedFunction,
  ResolvedParameter,
  ResolvedReturn,
  ResolvedInterface,
  ResolvedProperty,
  CategorizedModule,
  TaxonomyConfig
} from '../types/functions'
import defaultTaxonomy from '../config/functions-structure.json'

export function extractCommentText(comment?: TypeDocComment): string {
  if (!comment?.summary || comment.summary.length === 0) return ''
  return comment.summary.map(s => s.text).join('').trim()
}

export function extractBlockTag(comment: TypeDocComment | undefined, tagNames: string[]): string {
  if (!comment?.blockTags) return ''
  const tag = comment.blockTags.find(t =>
    tagNames.some(tn => t.tag.toLowerCase() === tn.toLowerCase() || t.tag.toLowerCase() === `@${tn.toLowerCase()}`)
  )
  if (!tag || !tag.content) return ''
  return tag.content.map(c => c.text).join('').trim()
}

export function extractExamples(comment?: TypeDocComment): string[] {
  if (!comment?.blockTags) return []
  return comment.blockTags
    .filter(t => t.tag.toLowerCase() === '@example' || t.tag.toLowerCase() === 'example')
    .map(t => t.content.map(c => c.text).join('').trim())
    .filter(Boolean)
}

export function formatTypeString(type?: TypeDocType): string {
  if (!type) return 'any'

  switch (type.type) {
    case 'intrinsic':
      return type.name || 'unknown'
    case 'reference': {
      const base = type.name || 'any'
      if (type.typeArguments && type.typeArguments.length > 0) {
        return `${base}<${type.typeArguments.map(formatTypeString).join(', ')}>`
      }
      return base
    }
    case 'union':
      return (type.types || []).map(formatTypeString).join(' | ') || 'any'
    case 'intersection':
      return (type.types || []).map(formatTypeString).join(' & ') || 'any'
    case 'array':
      return `${formatTypeString(type.elementType)}[]`
    case 'tuple':
      return `[${(type.elements || []).map(formatTypeString).join(', ')}]`
    case 'literal':
      return typeof type.value === 'string' ? `'${type.value}'` : String(type.value)
    case 'reflection':
      if (type.declaration?.signatures && type.declaration.signatures.length > 0) {
        const sig = type.declaration.signatures[0]
        const params = (sig.parameters || [])
          .map(p => `${p.name}: ${formatTypeString(p.type)}`)
          .join(', ')
        return `(${params}) => ${formatTypeString(sig.type)}`
      }
      if (type.declaration?.children && type.declaration.children.length > 0) {
        return `{ ${type.declaration.children.map(c => `${c.name}: ${formatTypeString(c.type)}`).join('; ')} }`
      }
      return 'object'
    case 'rest':
      return `...${formatTypeString(type.elementType)}`
    case 'typeOperator':
      return `${type.target || 'keyof'} ${formatTypeString(type.typeArguments?.[0])}`
    default:
      return type.name || type.type || 'any'
  }
}

/**
 * Builds a fast lookup map of interface and type declarations from TypeDoc data
 */
export function buildInterfaceMap(root: TypeDocRoot): Map<string, TypeDocNode> {
  const map = new Map<string, TypeDocNode>()

  function traverse(node: TypeDocNode) {
    // Kind 256 is Interface, 4194304 is TypeAlias, 128 is Class
    if (node.kind === 256 || node.kind === 4194304 || node.kind === 128) {
      map.set(node.name, node)
      map.set(String(node.id), node)
    }
    if (node.children) {
      for (const child of node.children) {
        traverse(child)
      }
    }
  }

  traverse(root)
  return map
}

/**
 * Resolves properties of an interface / type declaration
 */
export function resolveInterface(
  typeOrName: string | TypeDocType | undefined,
  interfaceMap: Map<string, TypeDocNode>,
  depth: number = 0
): ResolvedInterface | undefined {
  if (!typeOrName || depth > 3) return undefined

  let targetName: string | undefined
  if (typeof typeOrName === 'string') {
    targetName = typeOrName
  } else if (typeOrName.type === 'reference') {
    targetName = typeOrName.name
    // If it is a Promise or Array wrapper, unwrap the inner type
    if ((targetName === 'Promise' || targetName === 'Array') && typeOrName.typeArguments?.[0]) {
      return resolveInterface(typeOrName.typeArguments[0], interfaceMap, depth)
    }
  }

  if (!targetName) return undefined

  // Ignore standard built-in types
  const primitives = ['string', 'number', 'boolean', 'void', 'null', 'undefined', 'any', 'unknown', 'never', 'Record', 'Object', 'Function']
  if (primitives.includes(targetName)) return undefined

  const node = interfaceMap.get(targetName)
  if (!node || !node.children) return undefined

  const properties: ResolvedProperty[] = node.children
    .filter(child => child.kind === 1024 || child.kind === 32 || !!child.type) // Property / Variable
    .map(child => {
      const typeStr = formatTypeString(child.type)
      const isOptional = !!child.flags?.isOptional
      const desc = extractCommentText(child.comment)
      let nested: ResolvedProperty[] | undefined

      if (child.type && depth < 2) {
        const nestedIf = resolveInterface(child.type, interfaceMap, depth + 1)
        if (nestedIf && nestedIf.properties.length > 0) {
          nested = nestedIf.properties
        }
      }

      return {
        name: child.name,
        typeString: typeStr,
        rawType: child.type,
        optional: isOptional,
        defaultValue: child.defaultValue,
        description: desc,
        nestedProperties: nested
      }
    })

  return {
    id: node.id,
    name: node.name,
    description: extractCommentText(node.comment),
    properties
  }
}

/**
 * Generates an OpenAPI-style default JSON representation of a resolved interface
 */
export function generateInterfaceMockObject(resolved?: ResolvedInterface): any {
  if (!resolved || !resolved.properties) return {}

  const obj: Record<string, any> = {}
  for (const prop of resolved.properties) {
    if (prop.defaultValue) {
      try {
        obj[prop.name] = JSON.parse(prop.defaultValue)
        continue
      } catch {
        obj[prop.name] = prop.defaultValue
        continue
      }
    }

    const type = prop.typeString.toLowerCase()
    if (prop.nestedProperties && prop.nestedProperties.length > 0) {
      obj[prop.name] = generateMockFromProperties(prop.nestedProperties)
    } else if (type.includes('string')) {
      if (prop.name.toLowerCase().includes('url')) {
        obj[prop.name] = 'https://api.example.com/data'
      } else if (prop.name.toLowerCase().includes('method')) {
        obj[prop.name] = 'GET'
      } else if (prop.name.toLowerCase().includes('selector')) {
        obj[prop.name] = '#submit-button'
      } else {
        obj[prop.name] = `<${prop.name}>`
      }
    } else if (type.includes('number')) {
      if (prop.name.toLowerCase().includes('timeout')) {
        obj[prop.name] = 30000
      } else if (prop.name.toLowerCase().includes('status')) {
        obj[prop.name] = 200
      } else {
        obj[prop.name] = 1
      }
    } else if (type.includes('boolean')) {
      obj[prop.name] = true
    } else if (type.includes('record') || type.includes('object')) {
      obj[prop.name] = { key: 'value' }
    } else if (type.includes('[]') || type.includes('array')) {
      obj[prop.name] = []
    } else {
      obj[prop.name] = null
    }
  }
  return obj
}

function generateMockFromProperties(props: ResolvedProperty[]): any {
  const obj: Record<string, any> = {}
  for (const prop of props) {
    const type = prop.typeString.toLowerCase()
    if (type.includes('string')) {
      obj[prop.name] = `<${prop.name}>`
    } else if (type.includes('number')) {
      obj[prop.name] = 0
    } else if (type.includes('boolean')) {
      obj[prop.name] = true
    } else {
      obj[prop.name] = {}
    }
  }
  return obj
}

/**
 * Builds mock step execution JSON payload
 */
export function generateStepPayloadExample(
  funcName: string,
  isBrowser: boolean,
  parameters: ResolvedParameter[],
  rawExample?: string
): string {
  // If raw example provided in TSDoc is JSON or step format, prefer it
  if (rawExample) {
    const cleaned = rawExample.replace(/^```(json|ts|javascript)?\n?/, '').replace(/\n?```$/, '').trim()
    if (cleaned.startsWith('{') && cleaned.endsWith('}')) {
      try {
        const parsed = JSON.parse(cleaned)
        return JSON.stringify(parsed, null, 2)
      } catch {
        // Fall back to raw cleaned string
        return cleaned
      }
    }
  }

  // Synthesize standard Q-Play step object
  const stepFunctionName = isBrowser ? `browser:${funcName}` : funcName
  const argsObj: Record<string, any> = {}

  for (const param of parameters) {
    if (param.resolvedInterface) {
      argsObj[param.name] = generateInterfaceMockObject(param.resolvedInterface)
    } else {
      const type = param.typeString.toLowerCase()
      if (type.includes('string')) {
        if (param.name.toLowerCase().includes('url')) {
          argsObj[param.name] = 'https://api.example.com/data'
        } else if (param.name.toLowerCase().includes('selector')) {
          argsObj[param.name] = '#login-btn'
        } else if (param.name.toLowerCase().includes('key') || param.name.toLowerCase().includes('name')) {
          argsObj[param.name] = 'my_variable'
        } else {
          argsObj[param.name] = `sample_${param.name}`
        }
      } else if (type.includes('number')) {
        if (param.name.toLowerCase().includes('ms') || param.name.toLowerCase().includes('timeout') || param.name.toLowerCase().includes('sleep')) {
          argsObj[param.name] = 1000
        } else {
          argsObj[param.name] = 100
        }
      } else if (type.includes('boolean')) {
        argsObj[param.name] = true
      } else if (type.includes('record') || type.includes('object')) {
        argsObj[param.name] = { sampleKey: 'sampleValue' }
      } else {
        argsObj[param.name] = `{${param.name}}`
      }
    }
  }

  const step: Record<string, any> = {
    function: stepFunctionName,
    args: argsObj
  }

  // For data returning functions, add "as"
  if (!isBrowser && !['logVariable', 'logVariableType', 'triggerError', 'sleep'].includes(funcName)) {
    step.as = `${funcName}Result`
  }
  step.order = 1

  return JSON.stringify(step, null, 2)
}

/**
 * Normalizes and categorizes raw TypeDoc data into structured modules
 */
export function normalizeFunctionsData(
  root: TypeDocRoot,
  customTaxonomy: TaxonomyConfig = defaultTaxonomy as TaxonomyConfig
): CategorizedModule[] {
  const interfaceMap = buildInterfaceMap(root)
  const modules: CategorizedModule[] = []

  const topLevelChildren = root.children || []

  for (const moduleNode of topLevelChildren) {
    const moduleName = moduleNode.name
    const isBrowserModule = moduleName.toLowerCase().includes('browser')

    // Find classes or direct function members
    const allFunctions: ResolvedFunction[] = []

    function extractFromNode(node: TypeDocNode) {
      // If node is a class, inspect its methods
      if (node.kind === 128 && node.children) {
        for (const method of node.children) {
          if (method.kind === 2048 || method.signatures) {
            processFunctionNode(method, isBrowserModule, moduleName)
          }
        }
      } else if (node.kind === 2048 || node.kind === 64 || node.signatures) {
        processFunctionNode(node, isBrowserModule, moduleName)
      }
    }

    function processFunctionNode(funcNode: TypeDocNode, isBrowser: boolean, modName: string) {
      const signature = funcNode.signatures?.[0]
      const comment = signature?.comment || funcNode.comment
      const summary = extractCommentText(comment)
      const remarks = extractBlockTag(comment, ['remarks', 'description', 'notes'])
      const examples = extractExamples(comment)

      // Check for inline TSDoc category / group tag
      const inlineCategory = extractBlockTag(comment, ['category', 'group'])

      // Parameters
      const rawParams: TypeDocParameter[] = signature?.parameters || []
      const resolvedParameters: ResolvedParameter[] = rawParams.map(param => {
        const paramTypeStr = formatTypeString(param.type)
        const isOptional = !!param.flags?.isOptional
        const paramDesc = extractCommentText(param.comment)
        const resolvedIf = resolveInterface(param.type, interfaceMap)

        return {
          id: param.id,
          name: param.name,
          typeString: paramTypeStr,
          rawType: param.type,
          optional: isOptional,
          defaultValue: param.defaultValue,
          description: paramDesc,
          isComplexType: !!resolvedIf,
          resolvedInterface: resolvedIf
        }
      })

      // Return Type
      const returnType = signature?.type || funcNode.type
      const returnTypeStr = formatTypeString(returnType)
      const returnDesc = extractBlockTag(comment, ['returns', 'return'])
      const resolvedReturnIf = resolveInterface(returnType, interfaceMap)

      const returnModel: ResolvedReturn = {
        typeString: returnTypeStr,
        rawType: returnType,
        description: returnDesc,
        resolvedInterface: resolvedReturnIf
      }

      // Build signature string
      const paramSig = resolvedParameters
        .map(p => `${p.name}${p.optional ? '?' : ''}: ${p.typeString}`)
        .join(', ')
      const signatureString = `${funcNode.name}(${paramSig}): ${returnTypeStr}`

      // Determine Category
      let category = inlineCategory
      if (!category) {
        const moduleTaxonomy = customTaxonomy[modName]
        if (moduleTaxonomy) {
          for (const [catName, funcList] of Object.entries(moduleTaxonomy)) {
            if (funcList.includes(funcNode.name)) {
              category = catName
              break
            }
          }
        }
      }
      if (!category) {
        category = 'General'
      }

      // Badge
      let badge: 'FUNC' | 'BROWSER' | 'ASYNC' | 'MACRO' = isBrowser ? 'BROWSER' : 'FUNC'
      if (returnTypeStr.startsWith('Promise<') && !isBrowser) {
        badge = 'ASYNC'
      }

      // Unique ID & Anchor
      const slugModule = isBrowser ? 'browser' : 'basic'
      const id = `${slugModule}-${funcNode.name}`
      const anchorId = funcNode.name

      // Step & Response Payload Examples
      const stepPayloadExample = generateStepPayloadExample(
        funcNode.name,
        isBrowser,
        resolvedParameters,
        examples[0]
      )

      let responsePayloadExample: string | undefined
      if (resolvedReturnIf) {
        responsePayloadExample = JSON.stringify(generateInterfaceMockObject(resolvedReturnIf), null, 2)
      }

      const source = funcNode.sources?.[0]
        ? {
            fileName: funcNode.sources[0].fileName,
            line: funcNode.sources[0].line
          }
        : undefined

      allFunctions.push({
        id,
        anchorId,
        name: funcNode.name,
        displayName: isBrowser ? `browser:${funcNode.name}` : funcNode.name,
        module: modName,
        category,
        badge,
        signatureString,
        summary,
        description: summary,
        remarks,
        parameters: resolvedParameters,
        returns: returnModel,
        examples,
        stepPayloadExample,
        responsePayloadExample,
        source,
        rawNode: funcNode
      })
    }

    if (moduleNode.children) {
      for (const child of moduleNode.children) {
        extractFromNode(child)
      }
    } else {
      extractFromNode(moduleNode)
    }

    // Group functions by category according to taxonomy order
    const categoryMap = new Map<string, ResolvedFunction[]>()
    const taxonomyOrder = customTaxonomy[moduleName] ? Object.keys(customTaxonomy[moduleName]) : []

    // Initialize map with taxonomy order
    for (const catName of taxonomyOrder) {
      categoryMap.set(catName, [])
    }

    for (const fn of allFunctions) {
      if (!categoryMap.has(fn.category)) {
        categoryMap.set(fn.category, [])
      }
      categoryMap.get(fn.category)!.push(fn)
    }

    const categories = Array.from(categoryMap.entries())
      .filter(([_, fns]) => fns.length > 0)
      .map(([catName, fns]) => ({
        name: catName,
        functions: fns
      }))

    modules.push({
      name: moduleName,
      categories
    })
  }

  return modules
}
